export type PricingRow = {
  plan: string
  key: string         // stripePlans key ('1day'|'1week'|'monthly'|'3month'|'6month'|'annual')
  oneTime: boolean    // true → one-time pass (create-pass, mode: payment); false → subscription
  tradingDays: number
  price: number
  costPerDay: number
  vsMonthly: string   // cost/day as % of the monthly anchor's daily rate
  discountPct: number // % off vs buying the same months at the monthly price (0 for ≤ 1 month)
}

// ─────────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for all pricing. Change BASE and every derived number
// (anchor daily rate, "% vs anchor", multi-month discount %) recomputes. Tier prices
// are hand-set targets; cost/day, vs-anchor %, and discount % derive.
// Donation conversion is DELIBERATELY DECOUPLED from the subscription daily rate.
// ─────────────────────────────────────────────────────────────────────────────
const BASE = {
  monthlyPrice: 24.99,     // $/month — the anchor
  monthlyTradingDays: 21,  // 252 trading days/yr ÷ 12; granted per monthly cycle
}

// Donations convert at a SEPARATE daily rate (not the ~$0.95 subscription rate) so the 2×
// bonus stays sane. e.g. $100 / $2.50 × 2 = 80 premium days.
const DONATION_DAILY_RATE = 2.5

// Hand-set tier prices. Conservative term discounts (≤ ~15%).
const TIER_TARGETS: Array<{ plan: string; key: string; tradingDays: number; price: number }> = [
  { plan: '1 Day',   key: '1day',    tradingDays: 1,   price: 4.99 },
  { plan: '1 Week',  key: '1week',   tradingDays: 5,   price: 9.99 },
  { plan: '1 Month', key: 'monthly', tradingDays: BASE.monthlyTradingDays,      price: BASE.monthlyPrice }, // anchor
  { plan: '3 Month', key: '3month',  tradingDays: BASE.monthlyTradingDays * 3,  price: 69.99 },
  { plan: '6 Month', key: '6month',  tradingDays: BASE.monthlyTradingDays * 6,  price: 132.99 },
  { plan: 'Annual',  key: 'annual',  tradingDays: BASE.monthlyTradingDays * 12, price: 229 }, // 24% off (2026-09-27, was 254.99). Stripe: STRIPE_PRICE_ID_ANNUAL
]

const DAILY_RATE = round2(BASE.monthlyPrice / BASE.monthlyTradingDays) // anchor ≈ $1.19/day

function round2(n: number): number { return Math.round(n * 100) / 100 }

function toRow(t: { plan: string; key: string; tradingDays: number; price: number }): PricingRow {
  const months = t.tradingDays / BASE.monthlyTradingDays
  const costPerDay = round2(t.price / t.tradingDays)
  const isAnchor = t.plan === '1 Month'
  const pct = Math.round((costPerDay / DAILY_RATE) * 100)
  const discountPct = months > 1 ? Math.round((1 - t.price / (months * BASE.monthlyPrice)) * 100) : 0
  const oneTime = t.key === '1day' || t.key === '1week'
  return { plan: t.plan, key: t.key, oneTime, tradingDays: t.tradingDays, price: t.price, costPerDay, vsMonthly: isAnchor ? '100% (anchor)' : `${pct}%`, discountPct }
}

export const PRICING = {
  // Messaging labels (copy/history only; NOT used to date-gate anything).
  earlyAccessLabel: 'Early access',
  freeUntilLabel: 'Mar 2, 2026',
  freeUntilISO: '2026-03-02',
  preLaunchTimeMultiplier: 2, // legacy alias — donation math uses DONATION_MULTIPLIER (feature-flags)

  trialTradingDays: 5,
  freeTierDailyMinutes: 15,

  base: BASE,
  monthlyDailyRate: DAILY_RATE,                // subscription anchor $/day (display)
  donationDailyRate: DONATION_DAILY_RATE,      // DECOUPLED donation conversion denominator
  monthly: toRow(TIER_TARGETS.find((t) => t.plan === '1 Month')!), // anchor row (back-compat)
  // Named one-time passes — the cheap "pay as you go" doors (surfaced by the pass-visibility test).
  pass1Day: toRow(TIER_TARGETS.find((t) => t.key === '1day')!),
  pass1Week: toRow(TIER_TARGETS.find((t) => t.key === '1week')!),
  pricingRows: TIER_TARGETS.map(toRow) as PricingRow[],
}

// Built once: toLocaleString with options constructs a new formatter on EVERY call (108-175ms of phone
// CPU per page load from this one helper, register intl-formatter-per-call-client-1031).
const USD_FMT = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })

export function formatUSD(n: number) {
  return USD_FMT.format(n)
}
